/*
 * Used by Enterprise Report pages to sort tables by column
 */
import { useState, useMemo } from 'react'

export type SortDirection = 'asc' | 'desc'

export interface SortConfig<T> {
  key: keyof T
  direction: SortDirection
}

export interface UseSortingOptions<T> {
  data: T[]
  defaultSortKey: keyof T
  defaultDirection?: SortDirection
  customComparator?: (a: T, b: T, key: keyof T) => number
  onSortChange?: () => void // For pagination reset, etc.
}

export function useSorting<T>({
  data,
  defaultSortKey,
  defaultDirection = 'desc',
  customComparator,
  onSortChange,
}: UseSortingOptions<T>) {
  const [sortConfig, setSortConfig] = useState<SortConfig<T>>({
    key: defaultSortKey,
    direction: defaultDirection,
  })

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return data

    return [...data].sort((a, b) => {
      // Use custom comparator if provided
      if (customComparator) {
        const result = customComparator(a, b, sortConfig.key)
        return sortConfig.direction === 'asc' ? result : -result
      }

      // Default comparator logic
      const aValue = a[sortConfig.key]
      const bValue = b[sortConfig.key]

      // Handle null/undefined values - put them at the end
      if (aValue == null && bValue == null) return 0
      if (aValue == null) return 1
      if (bValue == null) return -1

      // Try numeric comparison first
      const aNum = Number(aValue)
      const bNum = Number(bValue)

      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum
      }

      // Try date comparison
      const aDate = new Date(aValue as any)
      const bDate = new Date(bValue as any)

      if (!Number.isNaN(aDate.getTime()) && !Number.isNaN(bDate.getTime())) {
        const aTime = aDate.getTime()
        const bTime = bDate.getTime()
        return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime
      }

      // Fall back to string comparison
      const aStr = String(aValue).toLowerCase()
      const bStr = String(bValue).toLowerCase()

      return sortConfig.direction === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr)
    })
  }, [data, sortConfig, customComparator])

  const handleSort = (key: keyof T) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }))
    onSortChange?.()
  }

  const getSortIndicator = (key: keyof T) => {
    if (sortConfig.key !== key) return ''
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓'
  }

  return {
    sortedData,
    sortConfig,
    handleSort,
    getSortIndicator,
  }
}
